import { useEffect, useRef, useMemo } from "react";
import { Search, Check, ChevronUp, Star } from "lucide-react";
import { cn } from "./feedConstants";

/* ─── Multi-select dropdown with search (for sources) ─── */
const MultiSelectDropdown = ({
  isOpen,
  onClose,
  onToggle,
  selected,
  options,
  searchValue,
  onSearchChange,
  label,
  icon: TriggerIcon,
  t,
  favoritedSources = [],
  onToggleFavorite,
}) => {
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    if (isOpen) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen, onClose]);

  const filtered = searchValue.trim()
    ? options.filter((o) =>
        o.label.toLowerCase().includes(searchValue.trim().toLowerCase()),
      )
    : options;

  const selectedCount = selected.size;
  const favSet = useMemo(
    () => new Set(favoritedSources.map((f) => f.source_value)),
    [favoritedSources],
  );

  return (
    <div ref={ref} className="relative">
      <button
        onClick={onToggle}
        className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200"
        style={{
          background: selectedCount > 0 ? t.activeBg : t.inputBg,
          border:
            selectedCount > 0
              ? `1px solid ${t.activeBorder}`
              : `1px solid ${t.inputBorder}`,
          color: selectedCount > 0 ? t.activeText : t.textSecondary,
          cursor: "pointer",
          minWidth: 120,
        }}
        onMouseEnter={(e) => {
          if (selectedCount === 0) {
            e.currentTarget.style.border = `1px solid ${t.hoverBorder}`;
            e.currentTarget.style.boxShadow = t.hoverBtnShadow;
          }
        }}
        onMouseLeave={(e) => {
          if (selectedCount === 0) {
            e.currentTarget.style.border = `1px solid ${t.inputBorder}`;
            e.currentTarget.style.boxShadow = "none";
          }
        }}
      >
        <TriggerIcon size={14} style={{ opacity: 0.6, flexShrink: 0 }} />
        <span className="truncate flex-1 text-left" style={{ fontSize: 12 }}>
          {selectedCount > 0
            ? `${selectedCount} source${selectedCount > 1 ? "s" : ""}`
            : label}
        </span>
        <ChevronUp
          size={14}
          style={{
            opacity: 0.4,
            flexShrink: 0,
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.15s ease",
          }}
        />
      </button>

      {isOpen && (
        <div
          className="absolute top-full left-0 mt-2 w-[260px] rounded-lg overflow-hidden z-50"
          style={{
            background: t.dropBg,
            border: `1px solid ${t.dropBorder}`,
            boxShadow: t.dropShadow,
          }}
        >
          <div className="p-2">
            <div className="relative">
              <Search
                size={12}
                style={{
                  position: "absolute",
                  left: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  opacity: 0.4,
                }}
              />
              <input
                type="text"
                autoFocus
                className="w-full pl-7 pr-3 py-1.5 rounded text-xs outline-none"
                placeholder="Search sources..."
                value={searchValue}
                onChange={(e) => onSearchChange(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: t.inputBg,
                  border: `1px solid ${t.inputBorder}`,
                  color: t.inputText,
                }}
              />
            </div>
          </div>

          <div
            className="max-h-[240px] overflow-y-auto pb-1"
            style={{ scrollbarWidth: "thin" }}
          >
            {filtered.length === 0 && (
              <div className="px-3 py-3 text-xs" style={{ color: t.textMuted }}>
                No sources found
              </div>
            )}
            {filtered.map((opt) => {
              const isSelected = selected.has(opt.key);
              return (
                <button
                  key={opt.key}
                  className="group/srcrow w-full text-left px-3 py-1.5 flex items-center gap-2.5 text-xs transition-colors"
                  style={{
                    cursor: "pointer",
                    color: isSelected ? t.activeText : t.textSecondary,
                    background: isSelected ? t.activeBg : "transparent",
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected)
                      e.currentTarget.style.background = t.dropHover;
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected)
                      e.currentTarget.style.background = "transparent";
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    const next = new Set(selected);
                    if (next.has(opt.key)) next.delete(opt.key);
                    else next.add(opt.key);
                    onClose.__updateSelected?.(next) || onClose(next);
                  }}
                >
                  <div
                    className="flex items-center justify-center w-4 h-4 rounded flex-shrink-0"
                    style={{
                      border: isSelected
                        ? `1px solid ${t.checkBorderActive}`
                        : `1px solid ${t.checkBorder}`,
                      background: isSelected ? t.checkBgActive : "transparent",
                    }}
                  >
                    {isSelected && (
                      <Check size={10} style={{ color: t.activeText }} />
                    )}
                  </div>
                  {opt.icon && (
                    <img
                      src={opt.icon}
                      alt=""
                      className="w-4 h-4 rounded flex-shrink-0"
                      style={{ objectFit: "cover" }}
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  )}
                  <span className="truncate flex-1">{opt.label}</span>
                  {onToggleFavorite && (
                    <Star
                      size={12}
                      className={cn(
                        "shrink-0 transition-all cursor-pointer",
                        favSet.has(opt.key)
                          ? "opacity-100"
                          : "opacity-0 group-hover/srcrow:opacity-60",
                      )}
                      style={{
                        color: favSet.has(opt.key)
                          ? "rgb(251,191,36)"
                          : t.textMuted,
                        fill: favSet.has(opt.key)
                          ? "rgb(251,191,36)"
                          : "transparent",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleFavorite(opt);
                      }}
                      title={
                        favSet.has(opt.key)
                          ? "Remove from favorites"
                          : "Add to favorites"
                      }
                    />
                  )}
                </button>
              );
            })}
          </div>

          {selectedCount > 0 && (
            <div
              className="px-2 pb-2 pt-1"
              style={{ borderTop: `1px solid ${t.divider}` }}
            >
              <button
                className="w-full text-center py-1 rounded text-xs transition-colors"
                style={{ color: t.clearText, background: t.clearBg }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = t.clearBgHover;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = t.clearBg;
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onClose(new Set());
                }}
              >
                Clear sources
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
